import React from "react";
import * as fuzzy from "fuzzy";
// @ts-ignore
import stringify from "json-align";
// @ts-ignore
import lodashMemoize from "lodash.memoize";
import lodashOnce from "lodash.once";
import lodashDebounce from "lodash.debounce";
import lodashThrottle from "lodash.throttle";
import fastCopy from "fast-copy";
import actualDeepEqual from "deep-equal";

import * as StringUtils from "./StringUtils";

import { RequestError } from "../api/ApiTypes";
import { ClassOf, Nullable, Provider } from "../types/UtilTypes";
// @ts-ignore

/***
 * Returns a promise that resolves to the value of func() when it is truthy.
 * Waits for a length of time (timeout, or forever if timeout is falsy) and polls
 * at an interval of poll to check whether the value returned by func() is truthy.
 *
 * Will call func() immediately and skip setting up an interval if it is already satisfied.
 */
export const waitFor = <T>(
	func: () => T | null | undefined | false,
	poll = 20,
	timeout: number | boolean = 1000,
): Promise<T> => {
	const alreadySatisfied = func();
	if (alreadySatisfied) return Promise.resolve(alreadySatisfied);
	const start = Date.now();

	const timeoutError = new Error("Timed out");
	return new Promise((resolve, reject) => {
		const intervalId = window.setInterval(() => {
			if (
				typeof timeout === "number" &&
				timeout >= 0 &&
				start + timeout < Date.now()
			) {
				window.clearInterval(intervalId);
				return reject(timeoutError);
			}
			const value = func();
			if (value) {
				window.clearInterval(intervalId);
				return resolve(value as any);
			}
		}, poll);
	});
};

export const arrayRandomElement = <T>(array: T[]): T => {
	return array[Math.floor(Math.random() * array.length)];
};

export const PASSIVE_EVENTS = [
	"scroll",
	"wheel",
	"touchstart",
	"touchmove",
	"touchenter",
	"touchend",
	"touchleave",
	"mouseout",
	"mouseleave",
	"mouseup",
	"mousedown",
	"mousemove",
	"mouseenter",
	"mousewheel",
	"mouseover",
];

/***
 * Force event listeners to be passive for mousewheel events
 */
export const forcePassiveListeners = () => {
	const addEventListener = EventTarget.prototype.addEventListener;
	EventTarget.prototype.addEventListener = function (
		this: HTMLElement,
		type,
		callback,
		options,
	) {
		if (
			options === true ||
			(typeof options === "object" && (options as any).passive)
		) {
			return addEventListener.call(this, type, callback, options);
		}

		const makePassive = PASSIVE_EVENTS.includes(type);
		let updatedOptions: any = options;

		const currentPassive =
			typeof options === "object" ? options.passive : options;
		if (makePassive && currentPassive === undefined) {
			updatedOptions =
				typeof options === "object"
					? Object.assign(options, { passive: true })
					: true;
		}

		return addEventListener.call(this, type, callback, updatedOptions);
	};
};

export const objectMap = <T extends {}>(
	obj: T,
	func: (key: keyof T, value: any) => [string, any],
) => {
	if (typeof obj !== "object")
		throw new Error("Must provide object to object map");
	const newObj: any = {};
	for (const key of Object.keys(obj)) {
		const [newKey, newValue] = func(key as any, (obj as any)[key]);
		if (newKey) {
			newObj[newKey] = newValue;
		}
	}
	return newObj;
};

let thrownOldBrowserError = false;

export const oldBrowserError = (error?: Error) => {
	if (!thrownOldBrowserError) {
		thrownOldBrowserError = true;
		console.error("Old browser detected", error);
	}
};

export const isIE10OrBelow = (): boolean => {
	// Have to do this with userAgent due to polyfills
	return isForceOldBrowser() || Boolean(navigator.userAgent.match(/MSIE/));
};

export const isForceOldBrowser = (): boolean => {
	return window.location.href.includes("oldBrowser");
};

export const isIE = (): boolean => {
	return (
		isForceOldBrowser() || Boolean(navigator.userAgent.match(/MSIE|Trident\//))
	);
};

/**
 * Loads a script and adds it to the page
 */
export const loadScript = (
	src: string,
	options: {
		async?: boolean;
		body?: boolean;
		id?: string;
		retry?: number;
	} = {},
) => {
	return new Promise<void>((resolve, reject) => {
		const tryLoad = (attempt: number) => {
			const resource = document.createElement("script");
			resource.setAttribute("data-loaded-script", "true");
			resource.async = options.async !== false;
			resource.defer = options.async !== false;
			resource.src = src;
			resource.id = options.id || `script-${StringUtils.uuid(6)}`;
			document[options.body ? "body" : "head"].appendChild(resource);
			let loaded = false;
			resource.onload = (resource as any).onreadystatechange = (e: Event) => {
				//console.log( this.readyState ); //uncomment this line to see which ready states are called.
				if (
					!loaded &&
					(e.type === "load" || (e as any).readyState === "complete")
				) {
					loaded = true;
					resolve();
				}
			};
			resource.onerror = (error) => {
				if (options.retry && attempt < options.retry) {
					tryLoad(attempt + 1);
				} else {
					console.error(error);
					reject(error);
				}
			};
		};

		tryLoad(0);
	});
};

export const nextFrame = (callback: (() => void) | undefined) => {
	if (!callback) {
		return undefined;
	}
	const raf = window.requestAnimationFrame;
	return raf ? raf(callback) : window.setTimeout(callback, 0);
};

export const getRelatedTarget = (
	e: Event & { relatedTarget?: Element },
): Element | null => {
	return e.relatedTarget || document.activeElement || null;
};

export const retryUntilResolved = <T>(
	func: () => Promise<T>,
	maxRetries: number,
): Promise<T> => {
	let tries = 0;

	const tryFunc = async (): Promise<T> => {
		return func().catch((e) => {
			if (maxRetries >= 0 && tries++ > maxRetries) {
				throw e;
			}
			return tryFunc();
		});
	};

	return tryFunc();
};

const floatToHex = (val: number) => {
	return ("0" + Math.round(val * 255).toString(16)).substr(-2);
};

const rgb2hex = (rgb: number[]) => {
	return "#" + floatToHex(rgb[0]) + floatToHex(rgb[1]) + floatToHex(rgb[2]);
};

export const applySaturationToHexColor = (hex: string, saturation: number) => {
	if (!/^#([0-9a-f]{6})$/i.test(hex)) {
		return hex;
	}

	const saturationFloat = saturation;
	const rgbIntensityFloat = [
		parseInt(hex.substr(1, 2), 16) / 255,
		parseInt(hex.substr(3, 2), 16) / 255,
		parseInt(hex.substr(5, 2), 16) / 255,
	];

	const rgbIntensityFloatSorted = rgbIntensityFloat
			.slice(0)
			.sort((a, b) => a - b),
		maxIntensityFloat = rgbIntensityFloatSorted[2],
		mediumIntensityFloat = rgbIntensityFloatSorted[1],
		minIntensityFloat = rgbIntensityFloatSorted[0];

	if (maxIntensityFloat === minIntensityFloat) {
		// All colors have same intensity, which means
		// the original color is gray, so we can't change saturation.
		return hex;
	}

	// New color max intensity wont change. Lets find medium and weak intensities.
	let newMediumIntensityFloat;
	const newMinIntensityFloat = maxIntensityFloat * (1 - saturationFloat);

	if (mediumIntensityFloat === minIntensityFloat) {
		// Weak colors have equal intensity.
		newMediumIntensityFloat = newMinIntensityFloat;
	} else {
		// Calculate medium intensity with respect to original intensity proportion.
		const intensityProportion =
			(maxIntensityFloat - mediumIntensityFloat) /
			(mediumIntensityFloat - minIntensityFloat);
		newMediumIntensityFloat =
			(intensityProportion * newMinIntensityFloat + maxIntensityFloat) /
			(intensityProportion + 1);
	}

	const newRgbIntensityFloat: number[] = [];
	const newRgbIntensityFloatSorted = [
		newMinIntensityFloat,
		newMediumIntensityFloat,
		maxIntensityFloat,
	];

	// We've found new intensities, but we have then sorted from min to max.
	// Now we have to restore original order.
	rgbIntensityFloat.forEach((originalRgb) => {
		const rgbSortedIndex = rgbIntensityFloatSorted.indexOf(originalRgb);
		newRgbIntensityFloat.push(newRgbIntensityFloatSorted[rgbSortedIndex]);
	});

	return rgb2hex(newRgbIntensityFloat);
};

export const changeTag = (node: Element, tag: string) => {
	const renamed = document.createElement(tag);
	for (let i = 0; i < node.attributes.length; i++) {
		const attribute = node.attributes.item(i);
		if (attribute) {
			renamed.setAttribute(attribute.nodeName, attribute.nodeValue as any);
		}
	}
	renamed.innerHTML = node.innerHTML;
	node.parentNode?.replaceChild(renamed, node);
	return renamed;
};

export const deepMerge = <A, B, C>(a: A, b: B): any => {
	if (a === undefined || a === null) return b;
	if (b === undefined || b === null) return a;

	if (typeof b !== "object" || typeof a !== "object") {
		return b;
	}

	const merged: any = {};

	function mergeKeys(keys: string[]) {
		for (const key of keys) {
			if (merged[key] === undefined) {
				const aValue = (a as any)[key];
				const bValue = (b as any)[key];
				merged[key] = deepMerge(aValue, bValue);
			}
		}
	}

	mergeKeys(Object.keys(a));
	mergeKeys(Object.keys(b));

	return merged;
};

export const isMobileDevice = () => {
	return /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile|Kindle|NetFront|Silk-Accelerated|(hpw|web)OS|Fennec|Minimo|Opera M(obi|ini)|Blazer|Dolfin|Dolphin|Skyfire|Zune/.test(
		navigator.userAgent,
	);
};

//type DiffType = "+" | "-" | "~";

export const diff = <A extends {}, B extends {}>(a: A, b: B): any => {
	const diffObj: any = {};
	const keySet = new Set([...Object.keys(a), ...Object.keys(b)]);
	keySet.forEach((key) => {
		const before = (a as any)[key];
		const after = (b as any)[key];

		let diff = null;

		if (before === undefined && after !== undefined) {
			diff = "+";
		} else if (before !== undefined && after === undefined) {
			diff = "-";
		} else if (after !== before) {
			diff = "~";
		}

		if (diff) {
			diffObj[key] = { diff, before, after };
		}
	});

	return diffObj;
};

export const preventDefault = (e: {
	preventDefault(): void;
	returnValue?: any;
}) => {
	e.returnValue = false;
	e.preventDefault();
};

const hasOwnProp = Object.prototype.hasOwnProperty;

export const get = (obj: any, path: string): any => {
	let result = obj;
	const keys = Array.isArray(path) ? path : path.split(".");
	for (let i = 0; i < keys.length; i++) {
		const key = keys[i];
		if (!result || !hasOwnProp.call(result, key)) {
			result = undefined;
			break;
		}
		result = result[key];
	}
	return result;
};

export const set = <T>(obj: any, path: string, value: T): T => {
	let result = obj;
	const keys = Array.isArray(path) ? path : path.split(".");
	// eslint-disable-next-line
	for (let i = 0; i < keys.length - 1; i++) {
		const key = keys[i];
		result = result[key] || (result[key] = {});
	}
	result[keys[keys.length - 1]] = value;
	return value;
};

export const isDebugHotkey = (e: Event | React.SyntheticEvent): boolean => {
	if (e instanceof KeyboardEvent) {
		return e.key === "`" || e.key === "§";
	}
	return false;
};

export const stopPropagation = (e: Event | React.SyntheticEvent) => {
	if (!isDebugHotkey(e)) {
		e.stopPropagation();
	}
};

export const overrideProperty = <T, K extends keyof T>(
	obj: T,
	key: K,
	overrider: (original: T[K]) => T[K],
) => {
	const original = obj[key];
	return (obj[key] = overrider.call(obj, original));
};

export const shallowEqual = <T>(
	a: T,
	b: T,
	options: { log?: boolean } = {},
): boolean => {
	if (Array.isArray(a) && Array.isArray(b)) {
		if (a.length !== b.length) {
			return false;
		}
		return a.every((av, i) => shallowEqual(av, b[i], options));
	}

	const result = ((a, b) => {
		if (a === b) return true;
		if (!(a instanceof Object) || !(b instanceof Object)) return a === b;

		const keys = Object.keys(a);
		const length = keys.length;

		for (let i = 0; i < length; i++) if (!(keys[i] in b)) return false;

		for (let i = 0; i < length; i++) {
			const av = (a as any)[keys[i]];

			const bv = (b as any)[keys[i]];
			if (av && av.valueOf && bv && bv.valueOf) {
				if (av.valueOf() !== bv.valueOf()) return false;
			} else {
				if (av !== bv) return false;
			}
		}

		return length === Object.keys(b).length;
	})(a, b);

	if (options.log) {
		// eslint-disable-next-line
		console.log(a, result ? "===" : "!==", b);
	}

	return result;
};

export const toNumber = (value: any, defaultValue = 0) => {
	if (typeof value !== "number") return defaultValue;
	if (isNaN(value)) return defaultValue;
	return value;
};

export const once = lodashOnce;
export const debounce = lodashDebounce;
export const throttle = lodashThrottle;
export const memoize: <A extends Array<any>, R>(
	func: (...args: A) => R,
	resolver?: (...args: A) => string | number,
) => (...args: A) => R = lodashMemoize as any;

/**
 * @deprecated
 */
export const old__debounce = (...args: any) => {
	return (target: any, key: string) => {
		if (key === undefined) {
			args.unshift(target);
			return lodashDebounce.apply(null, args as any);
		}

		args.unshift(target[key]);
		return (target[key] = lodashDebounce.apply(null, args as any));
	};
};

/**
 * @deprecated
 */
export const old__throttle = (...args: any[]) => {
	return (target: any, key?: string) => {
		if (key === undefined) {
			args.unshift(target);
			return lodashThrottle.apply(null, args as any);
		}

		args.unshift(target[key]);
		return (target[key] = lodashThrottle.apply(null, args as any));
	};
};

/**
 * @deprecated
 */
export const old__memoize = (argsMap: any) => {
	return (target: any, key: any) => {
		const func = key && typeof key === "string" ? target[key] : target;
		return (target[key || "__memoized"] = lodashMemoize(
			func,
			(...args: any[]) => {
				function isPrimitive(value: any) {
					return (
						value === null ||
						value === undefined ||
						typeof value === "string" ||
						typeof value === "number" ||
						typeof value === "boolean"
					);
				}

				const computedArgs = argsMap ? argsMap.apply(null, args) : args;
				if (!Array.isArray(computedArgs)) {
					throw new Error("Must return array for memoize() arg map");
				}
				try {
					if (computedArgs.length === 1 && isPrimitive(computedArgs[0])) {
						return computedArgs[0];
					}
					return JSON.stringify(computedArgs);
				} catch (error) {
					console.error(
						"Memoization failed! This likely means that an argument passed to a memoized function could not be stringified",
						computedArgs,
					);
					throw error;
				}
			},
		));
	};
};

export const isCharacterKeyPress = (evt: React.KeyboardEvent) => {
	if (typeof evt.which == "undefined") {
		// This is IE, which only fires keypress events for printable keys
		return true;
	} else if (typeof evt.which == "number" && evt.which > 0) {
		// In other browsers except old versions of WebKit, evt.which is
		// only greater than zero if the keypress is a printable key.
		// We need to filter out backspace and ctrl/alt/meta key combinations
		return !evt.ctrlKey && !evt.metaKey && !evt.altKey && evt.which !== 8;
	}
	return false;
};

export const outputDebug = (obj: any) => {
	window.__debug = obj;
	// eslint-disable-next-line
	console.log("Output assigned to window.__debug:");
	// eslint-disable-next-line
	console.log(obj);
};

export interface ResponseErrorInfo {
	code: string;
	identifier: string;
	message: string;
}

export const getAbsolutePosition = (
	el: Element,
): { top: number; left: number } => {
	const rect = el.getBoundingClientRect(),
		scrollLeft = window.pageXOffset || document.documentElement.scrollLeft,
		scrollTop = window.pageYOffset || document.documentElement.scrollTop;
	return { top: rect.top + scrollTop, left: rect.left + scrollLeft };
};

export interface SortKeysOptions<T> {
	compareFunc?: (a: string, b: string) => number;
	reducer?: (key: string) => Nullable<boolean | string>;
}

export const sortKeys = <T extends {}>(
	obj: T,
	options: SortKeysOptions<T> = {},
	parentPath?: string,
): T => {
	const { compareFunc, reducer } = options;
	if (Array.isArray(obj))
		return (obj as Array<any>)
			.sort(compareFunc)
			.map((value: any, index: number) => {
				if (value && typeof value === "object") {
					return sortKeys(
						value,
						options,
						`${parentPath ? parentPath + "." : ""}${index}`,
					);
				}
				return value;
			}) as any;
	return Object.keys(obj)
		.sort(compareFunc)
		.reduce((final, key) => {
			const value = (obj as any)[key];
			let reducedKey: any = key;
			if (reducer) {
				const reducerOutput = reducer(
					(parentPath ? parentPath + "." : "") + key,
				);
				if (typeof reducerOutput === "string") {
					reducedKey = reducerOutput;
				}
				if (!reducerOutput) {
					return final;
				}
			}

			if (value && typeof value !== "function" && typeof value === "object") {
				final[reducedKey] = sortKeys(
					value,
					options,
					(parentPath ? parentPath + "." : "") + reducedKey,
				);
			} else {
				final[reducedKey] = value;
			}
			return final;
		}, {} as any);
};

/**
 * @description Sorts an array of items with a list of priority items that will
 *              be brought to the top of the result. Useful for making sure
 *              certain important keys are displayed first, for example.
 */
export const sortPriority = <T>(
	items: T[],
	priorityItems: T[],
	baseSort?: (a: T, b: T) => number,
): T[] => {
	return items.sort((a, b) => {
		const pIndexA = priorityItems.indexOf(a);
		if (pIndexA !== -1) {
			const pIndexB = priorityItems.indexOf(b);
			return pIndexB === -1 ? -1 : pIndexA - pIndexB;
		} else {
			const pIndexB = priorityItems.indexOf(b);
			if (pIndexB !== -1) {
				return 1;
			}
		}
		return baseSort ? baseSort(a, b) : a > b ? 1 : -1;
	});
};

export const prettyStringify = (
	obj: any,
	keyReducer?: (key: string) => boolean,
): string => {
	return stringify(sortKeys(obj, { reducer: keyReducer }), null, 2);
};

export const clearSelection = () => {
	const selection = (document as any).selection;
	if (selection?.empty) {
		selection.empty();
	} else if (window.getSelection) {
		window.getSelection()?.removeAllRanges();
	}
};

export const deepEqual = actualDeepEqual;

export const deepClone: <T>(obj: T) => T = fastCopy;

export const getErrorIdentifier = (error: Error | RequestError): string => {
	const errorString = error.stack || JSON.stringify(error) + error.toString();

	// the uuid func on the backend requires nodejs so we use a different algo for the frontend ident
	const errorHash = StringUtils.hashCode(errorString);
	return btoa(errorHash.toString()).substr(0, 12).toLowerCase();
};

export const runAsync = <T>(func: () => Promise<T>): Promise<T> => func();

export const isValidDate = (date: any) => {
	return (
		typeof date !== "boolean" &&
		date instanceof Date &&
		!isNaN(date as any) &&
		date.getTime() !== -1 &&
		date.getTime() !== 0 &&
		date.getTime() < 99999999999999
	);
};

export const NEGATE_CHARS = ["!", "-"];

export const stringFilter = (
	inputs: string[],
	filters: string | { value: string },
	useFuzzy = false,
) => {
	/* eslint-disable no-param-reassign */
	if (typeof filters === "object") {
		filters = filters.value;
	}
	const filterList = filters.split(",").reduce((filters, filter) => {
		filter = filter.trim();
		if (filter) {
			filters.push(filter);
		}
		return filters;
	}, [] as string[]);
	if (filters.length === 0) {
		return true;
	}
	if (!Array.isArray(inputs)) {
		inputs = [inputs];
	}

	return filterList.every((baseFilter) => {
		let shouldShow = false;
		const isNegative = NEGATE_CHARS.some((char) => baseFilter.startsWith(char));
		const filter = isNegative ? baseFilter.substr(1) : baseFilter;

		if (isNegative && inputs.length === 0) return true;

		for (const input of inputs) {
			let matches = false;
			if (filter.startsWith(">") || filter.startsWith("<")) {
				const greaterThan = filter.startsWith(">");
				let start = filter.substr(1);
				const andEqual = start.startsWith("=");
				if (andEqual) {
					start = start.substr(1);
				}
				const startPos = parseFloat(start);
				const number = parseFloat(input);
				if (isNaN(number) || isNaN(startPos)) {
					matches = false;
				} else if (greaterThan) {
					matches = andEqual ? number >= startPos : number > startPos;
				} else {
					matches = andEqual ? number <= startPos : number < startPos;
				}
			} else {
				matches = useFuzzy
					? fuzzy.test(filter, input)
					: input.toLowerCase().includes(filter.toLowerCase());
			}
			if (matches && !isNegative) {
				shouldShow = true;
			}
			if (!matches && isNegative) {
				shouldShow = true;
			}
			if (matches && isNegative) {
				return false;
			}
		}
		return shouldShow;
	});
};

export const getValueSummary = (value: any): string => {
	if (value === null) return "null {}";
	if (value === undefined) return "undefined {}";
	if (Array.isArray(value)) {
		return `array {length=${value.length}}`;
	}
	if (typeof value === "object") {
		return `object {keys=${Object.keys(value).length}}`;
	}
	if (typeof value === "string") {
		return `string {length=${value.length}`;
	}
	return "number {}";
};

export const COLORS = {
	blue: "#5CA6FC",
	orange: "#F6BC4F",
	green: "#B8D85E",
	red: "#DC7495",
	purple: "#827CE0",
	coral: "#FB7551",
	aqua: "#61D3D0",
	yellow: "#FAD33F",
	grey: "#95B7D2",
};

export type ColorName = keyof typeof COLORS;

export const COLOR_NAMES: Array<ColorName> = Object.keys(COLORS) as any;

export const getColorName = (key: any): ColorName => {
	if (!key) return null as any;

	return COLOR_NAMES[
		Math.abs(StringUtils.hashCode(key?.toString() || Date.now().toString())) %
			COLOR_NAMES.length
	];
};

export const isPromise = <T>(obj: any): obj is Promise<T> => {
	return obj.then !== undefined;
};

export const noop: any = () => {};

export const callableClass = <T, F extends (this: T, ...args: any[]) => any>(
	clazz: ClassOf<T>,
	func: F,
): F & T => {
	func.prototype = clazz;
	return func as any;
};

export const scrollTo = (x = 0, y = 0, opts = {}) => {
	window.scroll({ left: x, top: y, behavior: "smooth" });
};

let headerElement: Element | null = null;

export const getHeaderHeight = () => {
	headerElement = headerElement || document.querySelector(".Header");
	return headerElement?.clientHeight || 0;
};

export const scrollToElement = (element: Element, offset = 10) => {
	const { top, left } = getAbsolutePosition(element);
	scrollTo(left, top - offset - getHeaderHeight());
};

export const resolveProvider = <T>(provider: Provider<T>): T => {
	if (typeof provider === "function") {
		return (provider as any)();
	}
	return provider;
};

export const nonUndefined = <T>(...args: T[]): T | undefined => {
	return args.find((arg) => arg !== undefined);
};

export const executeAsync = <F extends () => Promise<R>, R>(
	asyncFunc: F,
): Promise<R> => {
	return asyncFunc();
};

const ABSOLUTE_CHECK_REGEX = new RegExp("^(?:[a-z]+:)?//");

export const isRelativeUrl = (url: string) => {
	return !ABSOLUTE_CHECK_REGEX.test(url.toLowerCase());
};

export const isSafeUrl = (url: string) => {
	return !url.toLowerCase().includes("javascript");
};

export const isElementScrollable = (el?: HTMLElement | null) => {
	if (!el) {
		return false;
	}

	return el.scrollHeight > el.clientHeight;
};

export const isEmpty = (value: Object | Array<any>) => {
	if (Array.isArray(value)) {
		return value.length === 0;
	}

	return Object.keys(value).length === 0;
};
